import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Phone, Clock, LocateFixed, Loader2, Navigation } from "lucide-react";
import { WhatsAppIcon } from "../components/site/Icons";
import { PageHero } from "../components/site/PageHero";
import { AGENCIAS } from "../lib/site";
import { api } from "../lib/api";
import { distanceKm, fmtKm } from "../lib/stopPlaces";

const icon = (type, active) => L.divIcon({
  className: "",
  iconSize: [34, 34],
  iconAnchor: [17, 17],
  html: `<span style="display:grid;place-items:center;width:34px;height:34px;border-radius:999px;background:${type === "puntoenvio" ? "#0B110D" : type === "agencia" ? "#1E7A3C" : "#0B5323"};border:3px solid ${active ? "#22FF00" : "#fff"};box-shadow:0 8px 20px rgba(0,0,0,.3)"><span style="width:10px;height:10px;border-radius:999px;background:${type === "puntoenvio" ? "#22FF00" : "#fff"}"></span></span>`,
});

const FILTERS = [["todas", "Todas"], ["boleteria", "Terminales"], ["agencia", "Agencias"], ["puntoenvio", "PuntoEnvío"]];
const LABEL = { boleteria: "Boletería", agencia: "Agencia", puntoenvio: "PuntoEnvío" };
const waLink = (n) => `https://wa.me/549${n.replace(/\D/g, "")}?text=${encodeURIComponent("Hola, quiero consultar por pasajes de Santiagueño")}`;


const AgencyItem = ({ a, active, onClick }) => (
  <div data-testid={`agency-item-${a.id}`} onClick={onClick} className={`w-full cursor-pointer rounded-2xl p-5 text-left transition-colors duration-300 ${active ? "bg-[var(--forest)] text-white" : "bg-white hover:bg-[#ebeae2]"}`}>
    <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${active ? "text-[var(--lime)]" : "text-[var(--forest)]"}`}>{LABEL[a.type]} · {a.city}{a.km != null ? ` · a ${fmtKm(a.km)}` : ""}</span>
    <span className="mt-1 block font-display-up text-lg">{a.name}</span>
    <span className={`mt-2 flex items-start gap-2 text-sm ${active ? "text-white/85" : "text-[var(--muted-ink)]"}`}><MapPin className="mt-0.5 h-4 w-4 shrink-0" /> {a.address}</span>
    {a.whatsapp && (
      <a href={waLink(a.whatsapp)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`agency-whatsapp-${a.id}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-bold text-[var(--ink)]">
        <WhatsAppIcon className="h-4 w-4" /> {a.whatsapp}
      </a>
    )}
    {a.phone && <a href={`tel:${a.phone.replace(/\s/g, "")}`} onClick={(e) => e.stopPropagation()} className={`mt-3 inline-flex items-center gap-2 text-sm font-semibold ${active ? "text-white" : "text-[var(--forest)]"}`}><Phone className="h-4 w-4" /> {a.phone}</a>}
    {!a.whatsapp && !a.phone && <span className={`mt-2 block text-sm ${active ? "text-white/80" : "text-[var(--muted-ink)]"}`}>Consultas por la línea central de WhatsApp.</span>}
    <span data-testid={`agency-hours-${a.id}`} className={`mt-3 flex items-start gap-2 text-sm ${active ? "text-white/85" : "text-[var(--muted-ink)]"}`}><Clock className="mt-0.5 h-4 w-4 shrink-0" /> {a.hours || "Horario: consultalo por WhatsApp"}</span>
    <a href={`https://www.google.com/maps/dir/?api=1&destination=${a.lat},${a.lng}&travelmode=driving`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`agency-directions-${a.id}`} className={`mt-4 inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-bold transition-colors duration-300 ${active ? "border-[var(--lime)] text-[var(--lime)] hover:bg-[var(--lime)] hover:text-[var(--ink)]" : "border-[var(--forest)] text-[var(--forest)] hover:bg-[var(--forest)] hover:text-white"}`}>
      <Navigation className="h-4 w-4" /> Cómo llegar
    </a>
  </div>
);

export default function Agencias() {
  const [filter, setFilter] = useState("todas");
  const [active, setActive] = useState(null);
  const mapRef = useRef(null);
  const [all, setAll] = useState(AGENCIAS);
  useEffect(() => {
    let alive = true;
    api.get("/agencies").then((r) => { if (alive && Array.isArray(r.data) && r.data.length) setAll(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const filtered = useMemo(() => all.filter((a) => filter === "todas" || a.type === filter), [filter, all]);
  const [me, setMe] = useState(null);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState("");
  const list = useMemo(() => {
    if (!me) return filtered;
    return filtered.map((a) => ({ ...a, km: distanceKm(me, a) })).sort((x, y) => x.km - y.km);
  }, [filtered, me]);

  const locate = () => {
    if (!navigator.geolocation) return setGeoError("Tu dispositivo no permite compartir la ubicación.");
    setLocating(true); setGeoError("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const pos = { lat: coords.latitude, lng: coords.longitude };
        setMe(pos);
        setLocating(false);
        const near = filtered.map((a) => ({ ...a, km: distanceKm(pos, a) })).sort((x, y) => x.km - y.km)[0];
        if (near) {
          setActive(near.id);
          mapRef.current?.flyToBounds(L.latLngBounds([[pos.lat, pos.lng], [near.lat, near.lng]]), { padding: [60, 60], duration: 1.2, maxZoom: 14 });
        }
      },
      () => { setLocating(false); setGeoError("No pudimos obtener tu ubicación. Revisá los permisos del navegador."); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const focus = (a) => {
    setActive(a.id);
    mapRef.current?.flyTo([a.lat, a.lng], 13, { duration: 1.2 });
  };

  useEffect(() => {
    const m = mapRef.current;
    if (m && filtered.length) m.flyToBounds(L.latLngBounds(filtered.map((a) => [a.lat, a.lng])), { padding: [40, 40], duration: 1 });
  }, [filtered]);

  return (
    <>
      <PageHero testid="agencias-hero" eyebrow="Boleterías y agencias" lines={["Una boletería", "a la vuelta."]} intro="Encontrá dónde comprar tu pasaje o despachar tu encomienda PuntoEnvío." />
      <section className="bg-[var(--paper)] pb-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="mb-6 flex flex-wrap gap-2">
            {FILTERS.map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`agency-filter-${v}`} className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-300 ${filter === v ? "bg-[var(--ink)] text-white" : "bg-white hover:bg-[#ebeae2]"}`}>{l}</button>
            ))}
            <button onClick={locate} disabled={locating} data-testid="agency-locate-button" className="inline-flex items-center gap-2 rounded-full bg-[var(--lime)] px-5 py-2.5 text-sm font-bold text-[var(--ink)] transition-transform duration-300 hover:-translate-y-0.5 sm:ml-auto">
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />} {me ? "Actualizar mi ubicación" : "La más cercana a mí"}
            </button>
          </div>
          {geoError && <p data-testid="agency-geo-error" className="-mt-3 mb-5 text-sm font-medium text-red-700">{geoError}</p>}
          {me && list[0] && (
            <div data-testid="agency-nearest-banner" className="-mt-2 mb-5 flex flex-wrap items-center gap-2 rounded-2xl bg-[var(--forest)] px-5 py-4 text-white">
              <LocateFixed className="h-5 w-5 text-[var(--lime)]" />
              <span>La más cercana: <b>{list[0].name}</b> · a {fmtKm(list[0].km)}</span>
            </div>
          )}
          <div className="grid gap-4 lg:grid-cols-12">
            <div className="order-1 h-[380px] overflow-hidden rounded-[28px] ring-1 ring-black/5 md:h-[520px] lg:order-2 lg:col-span-8 lg:h-[680px]" data-lenis-prevent data-testid="agencies-map">
              <MapContainer ref={mapRef} center={[-31, -62]} zoom={5} scrollWheelZoom className="h-full w-full">
                <TileLayer attribution='&copy; OpenStreetMap' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="map-tiles" />
                {list.map((a) => (
                  <Marker key={a.id} position={[a.lat, a.lng]} icon={icon(a.type, active === a.id)} eventHandlers={{ click: () => setActive(a.id) }}>
                    <Popup><b>{a.name}</b><br />{a.address}{a.whatsapp ? <><br />WhatsApp {a.whatsapp}</> : null}</Popup>
                  </Marker>
                ))}
                {me && <CircleMarker center={[me.lat, me.lng]} radius={9} pathOptions={{ color: "#ffffff", weight: 3, fillColor: "#2563EB", fillOpacity: 1 }}><Popup>Estás acá</Popup></CircleMarker>}
              </MapContainer>
            </div>
            <div className="order-2 space-y-3 lg:order-1 lg:col-span-4 lg:max-h-[680px] lg:overflow-y-auto lg:pr-1" data-lenis-prevent>
              {list.map((a) => <AgencyItem key={a.id} a={a} active={active === a.id} onClick={() => focus(a)} />)}
            </div>
          </div>
          <p className="mt-4 text-xs text-[var(--muted-ink)]">Datos de la red oficial de agencias y boleterías. Consultá horarios de atención con cada punto.</p>
        </div>
      </section>
    </>
  );
}
